<template>
  <div v-if="show" class="modal-overlay modal-overlay-elevated" @click.self="$emit('cancel')">
    <!-- 2FA Verification Dialog -->
    <div class="modal-content max-w-md" role="dialog" aria-modal="true" aria-labelledby="2fa-verify-title">
      <div class="modal-header">
        <div class="flex items-center">
          <div class="flex-shrink-0 w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mr-4">
            <svg class="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h3 class="text-section-header" id="2fa-verify-title">Verify Two-Factor Authentication</h3>
        </div>
        <button @click="$emit('cancel')" class="modal-close-button" aria-label="Close dialog">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div class="modal-body space-y-6">
        <!-- Step indicator -->
        <div class="flex items-center justify-center space-x-4 mb-6">
          <div class="flex items-center">
            <div class="w-8 h-8 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-medium">✓</div>
            <span class="ml-2 text-sm font-medium text-gray-900">Setup</span>
          </div>
          <div class="w-16 h-0.5 bg-primary-500"></div>
          <div class="flex items-center">
            <div class="w-8 h-8 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-medium">2</div>
            <span class="ml-2 text-sm font-medium text-gray-900">Verify</span>
          </div>
        </div>

        <!-- Instructions -->
        <div class="text-center">
          <p class="text-body text-gray-600 mb-6">
            Enter the 6-digit code from your authenticator app to complete the setup.
          </p>
        </div>

        <!-- Code Input -->
        <form @submit.prevent="handleVerify" class="space-y-4">
          <div>
            <label for="verification-code" class="text-label block mb-2">
              Verification Code
            </label>
            <input
              id="verification-code"
              v-model="verificationCode"
              type="text"
              inputmode="numeric"
              pattern="[0-9]*"
              maxlength="6"
              placeholder="123456"
              class="w-full px-4 py-3 text-center text-2xl font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              :class="{
                'border-red-300 focus:ring-red-500 focus:border-red-500': error,
                'border-green-300 focus:ring-green-500 focus:border-green-500': success
              }"
              :disabled="loading"
              autocomplete="one-time-code"
              ref="codeInput"
            />
            <p v-if="error" class="mt-2 text-sm text-red-600" role="alert">
              {{ error }}
            </p>
            <p v-if="success" class="mt-2 text-sm text-green-600" role="alert">
              {{ success }}
            </p>
          </div>

          <!-- Code tips -->
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 class="text-sm font-medium text-blue-900 mb-2">💡 Tips</h4>
            <ul class="text-sm text-blue-700 space-y-1 list-disc list-inside">
              <li>Open your authenticator app (Google Authenticator, Authy, etc.)</li>
              <li>Find the Meal-Mate entry</li>
              <li>Enter the current 6-digit code</li>
              <li>Codes refresh every 30 seconds</li>
            </ul>
          </div>

          <!-- Hidden submit button for form submission -->
          <button type="submit" class="hidden" />
        </form>
      </div>

      <div class="modal-footer">
        <BaseButton variant="secondary" @click="$emit('cancel')" :disabled="loading">
          Cancel
        </BaseButton>
        <BaseButton 
          variant="primary" 
          @click="handleVerify" 
          :disabled="!isValidCode || loading"
          :loading="loading"
        >
          Verify & Complete Setup
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import BaseButton from '../BaseButton.vue'
import { useBodyScrollLock } from '../../composables/useBodyScrollLock'

const props = defineProps<{
  show: boolean
  loading?: boolean
  error?: string
  success?: string
}>()
useBodyScrollLock(() => props.show)

const emit = defineEmits<{
  (e: 'cancel'): void
  (e: 'verify', code: string): void
}>()

const verificationCode = ref('')
const codeInput = ref<HTMLInputElement>()

const isValidCode = computed(() => {
  return /^\d{6}$/.test(verificationCode.value)
})

const handleVerify = () => {
  if (isValidCode.value && !props.loading) {
    emit('verify', verificationCode.value)
  }
}

// Auto-submit when 6 digits are entered
watch(verificationCode, (newCode) => {
  // Remove non-digit characters
  const cleanCode = newCode.replace(/\D/g, '')
  if (cleanCode !== newCode) {
    verificationCode.value = cleanCode
  }
  
  // Auto-submit when we have 6 digits
  if (cleanCode.length === 6) {
    nextTick(() => {
      handleVerify()
    })
  }
})

// Focus input when modal opens
watch(() => props.show, (isShow) => {
  if (isShow) {
    nextTick(() => {
      codeInput.value?.focus()
      verificationCode.value = ''
    })
  }
})

// Clear code when there's an error
watch(() => props.error, (hasError) => {
  if (hasError) {
    nextTick(() => {
      codeInput.value?.focus()
      codeInput.value?.select()
    })
  }
})
</script>

<style scoped>
/* Additional input focus styles */
input:focus {
  outline: none;
}

/* Custom number input styling */
input[type="text"]::-webkit-outer-spin-button,
input[type="text"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="text"] {
  -moz-appearance: textfield;
}
</style>
